import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { ButtonIcon, H4Regular } from '@comeon/ui';
import { Modal } from '../../../../modal/modal-old';
import Portal from '../../../components/sportsbook-portal';
import LoadingSpinner from '../../components/sportsbook-loading-spinner';
import breakpoint from '../../../../../utils/style-utils';
import Icon, { ICON_CLOSE } from '../../../../icon';
import { isAddToBetslipMessageData } from './sportsbook-bet-builder-types';
import { Translation } from '../../../../translation/types';
import BetBuilderTextKeys from './sportsbook-bet-builder-modal-text-keys';

// added due to 888 franchise not having --base-11 variable
const StyledModal = styled(Modal)`
	background-color: rgba(var(--modal-wrapper-dark-bg, var(--base-11)), 0.85);
`;

const Wrapper = styled.div`
	margin: 0;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: calc(100% - 48px);
	height: calc(100% - 48px);

	overflow: hidden;
	background-color: var(--sportsbook-bg-primary);
	border-radius: 12px;
	filter: drop-shadow(5px 10px 25px rgba(0, 0, 0, 0.15));

	display: flex;
	flex-direction: column;

	${breakpoint.lg`
		max-width: 800px;
		max-height: 800px;
	`}
`;

const Header = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 10px;
	border-bottom: 1px solid var(--sportsbook-border-primary);
	flex: 0 0 auto;
`;
const HeaderLabel = styled(H4Regular)`
	padding-left: 30px;
	text-align: center;
	flex: 1 1 auto;
`;
const CloseIcon = styled(Icon).attrs({ type: ICON_CLOSE, height: 30, width: 30 })`
	fill: var(--sportsbook-betbuilder-close-icon, var(--sportsbook-icon-primary));
`;
const CloseButton = styled(ButtonIcon)`
	flex: 0 0 auto;
`;

const Body = styled.div`
	flex: 1 1 auto;
	position: relative;
`;

const LoadingSpinnerWrapper = styled.div`
	display: flex;
	height: 100%;
	width: 100%;
	position: absolute;
	z-index: 1;
	align-items: center;
	justify-content: center;
	background-color: var(--sportsbook-bg-primary);
`;

const StyledLoadingSpinner = styled(LoadingSpinner)`
	color: var(--sportsbook-text-main);
`;

const IFrame = styled.iframe`
	position: absolute;
	height: 100%;
	width: 100%;
`;

type BetBuilderModalProps = {
	translations: Translation;
	iframeUrl: string | null;
	onClose: () => void;
	onAddToBetslip: (betId: string) => void;
	isLoading: boolean;
};

const BetBuilderModal: React.FC<BetBuilderModalProps> = ({
	translations,
	iframeUrl,
	onClose,
	onAddToBetslip,
	isLoading
}) => {
	const widgetContainerRef = useRef<HTMLIFrameElement>(null);

	useEffect(() => {
		const handleMessage = (message: MessageEvent<string>) => {
			if (message.source !== widgetContainerRef.current?.contentWindow && message.data) {
				return;
			}

			const data = JSON.parse(message.data) as object;
			if (!isAddToBetslipMessageData(data)) {
				return;
			}

			onAddToBetslip(data.betslipUid);
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [onAddToBetslip]);

	const [isIframeLoaded, setIsIframeLoaded] = useState(false);
	const handleIframeLoaded = useCallback(() => {
		setIsIframeLoaded(true);
	}, [setIsIframeLoaded]);

	return (
		<Portal>
			<StyledModal show blurBg regOnly instant lockBodyScroll>
				<Wrapper>
					<Header>
						<HeaderLabel>{translations[BetBuilderTextKeys.MODAL_HEADER_LABEL]}</HeaderLabel>
						<CloseButton onClick={onClose}>
							<CloseIcon type={ICON_CLOSE} />
						</CloseButton>
					</Header>
					<Body>
						{(isLoading || !isIframeLoaded) && (
							<LoadingSpinnerWrapper>
								<StyledLoadingSpinner />
							</LoadingSpinnerWrapper>
						)}
						{!isLoading && !!iframeUrl && (
							<IFrame
								id="widgethost"
								ref={widgetContainerRef}
								title="BetBuilder"
								src={iframeUrl}
								onLoad={handleIframeLoaded}
							/>
						)}
					</Body>
				</Wrapper>
			</StyledModal>
		</Portal>
	);
};
export default BetBuilderModal;
